import { useEffect, useRef, useState } from "react";
import { Microphone, Stop, ArrowClockwise } from "@phosphor-icons/react";
import { Capacitor } from "@capacitor/core";

/**
 * VoiceRecorder
 * - Native (iOS / Android): uses `capacitor-voice-recorder` plugin (records to m4a/aac).
 *   This is REQUIRED on iOS WKWebView, where the web `MediaRecorder` API is
 *   either missing or produces 0-byte blobs — historically the silent-failure
 *   "tap stop and nothing happens" bug.
 * - Web: uses browser `MediaRecorder` (webm/opus). Works in Chrome/Safari/Firefox.
 * - On stop: calls onSubmit(blob, durationSec) — same signature for both paths.
 */
export default function VoiceRecorder({ onSubmit, busy }) {
  const isNative = Capacitor.isNativePlatform();
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const [stopping, setStopping] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(0);

  useEffect(() => {
    return () => stopStream();
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Convert base64 (returned by capacitor-voice-recorder) → Blob
  // so the Dashboard upload code can stay platform-agnostic.
  const base64ToBlob = (base64, mimeType) => {
    const byteChars = atob(base64);
    const byteArrays = [];
    const sliceSize = 1024;
    for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
      const slice = byteChars.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i += 1) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: mimeType || "audio/m4a" });
  };

  const logTelemetry = async (event, data) => {
    // Fire-and-forget telemetry to /api/errors so we can diagnose recorder
    // failures on real devices without needing the user to share Xcode logs.
    // Posts to BOTH preview and production backends, since iOS Codemagic builds
    // may be wired to production (apphush.online) but I (main agent) can only
    // read preview MongoDB directly. Whichever backend the user is logged into
    // will accept the post — the other will just 401/404 silently.
    const payload = {
      message: `[recorder] ${event}: api=${typeof window !== "undefined" ? (window.__HUSH_API_URL__ || "?") : "?"} | ${JSON.stringify(data || {}).slice(0, 600)}`,
      url: typeof window !== "undefined" ? window.location.href : "",
      ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
      platform: Capacitor.getPlatform(),
      app_version: "1.1.0",
    };
    const endpoints = [
      "https://momentum-app-64.preview.emergentagent.com/api/errors",
      "https://apphush.online/api/errors",
    ];
    await Promise.allSettled(
      endpoints.map((u) =>
        fetch(u, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          credentials: "omit",
        }).catch(() => null)
      )
    );
  };

  const startNative = async () => {
    const { VoiceRecorder } = await import("capacitor-voice-recorder");
    const can = await VoiceRecorder.canDeviceVoiceRecord();
    await logTelemetry("can", can);
    if (!can?.value) {
      setError("This device can't record audio.");
      return false;
    }
    let has = await VoiceRecorder.hasAudioRecordingPermission();
    await logTelemetry("has-perm", has);
    if (!has?.value) {
      const req = await VoiceRecorder.requestAudioRecordingPermission();
      await logTelemetry("perm-req", req);
      if (!req?.value) {
        setError("Microphone access denied. Enable it in Settings → Hush → Microphone.");
        return false;
      }
      // iOS sometimes returns `granted: true` from the prompt but the underlying
      // `AVAudioSession.recordPermission` flag hasn't propagated yet. If we call
      // startRecording immediately the plugin sees `.undetermined` and rejects
      // with MISSING_PERMISSION. Wait briefly and re-check, retrying up to 4x.
      for (let i = 0; i < 4; i += 1) {
        await new Promise((r) => setTimeout(r, 200));
        has = await VoiceRecorder.hasAudioRecordingPermission();
        if (has?.value) break;
      }
      await logTelemetry("has-perm-after-wait", has);
      if (!has?.value) {
        // iOS still says no — surface a softer message that tells the user the
        // permission was granted, just tap again. On the second tap, the OS
        // will have caught up.
        setError("Permission granted. Tap record again to start.");
        return false;
      }
    }
    let started;
    try {
      started = await VoiceRecorder.startRecording();
    } catch (e) {
      const msg = String(e?.message || e);
      // If the plugin already has a recorder cached (ALREADY_RECORDING),
      // there's a stale recording session from a previous tap. Stop+discard
      // it, then retry — otherwise every subsequent tap fails forever.
      if (msg.includes("ALREADY_RECORDING")) {
        await logTelemetry("start-throw-stale-cleanup", { message: msg });
        try {
          await VoiceRecorder.stopRecording();
        } catch (_) { /* discard */ }
        await new Promise((r) => setTimeout(r, 200));
        try {
          started = await VoiceRecorder.startRecording();
        } catch (e2) {
          await logTelemetry("start-throw-final", { message: String(e2?.message || e2) });
          setError("Recorder is stuck. Force-close and reopen the app.");
          return false;
        }
      } else {
        // Plugin throws MISSING_PERMISSION as a rejected promise on iOS when the
        // permission grant hasn't propagated. Retry once after a short wait.
        await logTelemetry("start-throw-retry", { message: msg });
        await new Promise((r) => setTimeout(r, 400));
        try {
          started = await VoiceRecorder.startRecording();
        } catch (e2) {
          await logTelemetry("start-throw-final", { message: String(e2?.message || e2) });
          setError("Permission granted. Tap record again to start.");
          return false;
        }
      }
    }
    await logTelemetry("start", started);
    if (started && started.value === false) {
      setError("Couldn't access the microphone. Close other audio apps and try again.");
      return false;
    }
    return true;
  };

  const startWeb = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : "audio/webm";
    const mr = new MediaRecorder(stream, { mimeType: mime });
    mediaRecorderRef.current = mr;
    chunksRef.current = [];
    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    mr.onstop = async () => {
      const dur = (Date.now() - startTimeRef.current) / 1000;
      stopStream();
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      if (blob.size < 1000) {
        setError("Recording too short. Try again.");
        setRecording(false);
        setElapsed(0);
        return;
      }
      setRecording(false);
      setElapsed(0);
      await onSubmit(blob, dur);
    };
    mr.start();
  };

  const start = async () => {
    setError("");
    try {
      let ok = true;
      if (isNative) {
        ok = await startNative();
      } else {
        await startWeb();
      }
      if (!ok) return;
      startTimeRef.current = Date.now();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        const e = (Date.now() - startTimeRef.current) / 1000;
        setElapsed(e);
        if (e >= 600) {
          // 10 min cap
          stop();
        }
      }, 200);
    } catch (e) {
      setError("Microphone access denied. Please allow mic permissions and retry.");
      console.error("[recorder] start failed:", e);
      await logTelemetry("start-exception", { message: String(e?.message || e) });
    }
  };

  const stopNative = async () => {
    const { VoiceRecorder } = await import("capacitor-voice-recorder");
    const result = await VoiceRecorder.stopRecording();
    // result.value: { recordDataBase64, msDuration, mimeType }
    const v = result?.value || {};
    // Use wall-clock elapsed time as a fallback if the plugin doesn't return
    // msDuration (some Android OEMs don't populate it correctly).
    const wallSec = startTimeRef.current ? (Date.now() - startTimeRef.current) / 1000 : 0;
    const pluginSec = (v.msDuration || 0) / 1000;
    const durSec = pluginSec > 0 ? pluginSec : wallSec;
    const b64Len = (v.recordDataBase64 || "").length;
    // Telemetry: log what the plugin actually returned so we can debug "too short"
    // on real devices. msDuration=0 + valid base64 = the bug we're trying to find.
    await logTelemetry("stop-result", {
      hasB64: Boolean(v.recordDataBase64),
      b64Len,
      pluginMs: v.msDuration,
      wallSec: Math.round(wallSec * 100) / 100,
      mimeType: v.mimeType,
    });
    if (!v.recordDataBase64 || b64Len < 1000) {
      // Genuinely no audio — keep the message but don't gate on duration alone.
      setError("Recording too short. Try again.");
      setRecording(false);
      setElapsed(0);
      return;
    }
    if (durSec < 0.5) {
      // Plugin returned audio but says it was <500ms even though the user held
      // record. This is the iOS plugin bug — use wall-clock instead.
      const blob = base64ToBlob(v.recordDataBase64, v.mimeType);
      setRecording(false);
      setElapsed(0);
      await onSubmit(blob, Math.max(1, wallSec));
      return;
    }
    const blob = base64ToBlob(v.recordDataBase64, v.mimeType);
    setRecording(false);
    setElapsed(0);
    await onSubmit(blob, durSec);
  };

  const stop = async () => {
    if (stopping) return;
    setStopping(true);
    try {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (isNative) {
        await stopNative();
      } else if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }
    } catch (e) {
      setError("Couldn't save your recording. Please try again.");
      setRecording(false);
      setElapsed(0);
      console.error("[recorder] stop failed:", e);
    } finally {
      setStopping(false);
    }
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const ss = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${ss}`;
  };

  return (
    <div className="card p-8 sm:p-10 grain" data-testid="voice-recorder">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="eyebrow">Today's entry</p>
          <h3 className="font-serif text-2xl sm:text-3xl mt-2">
            {recording ? "Listening…" : busy ? "Processing your voice" : "What's on your mind?"}
          </h3>
        </div>
        <div className="text-ink-500 font-serif text-lg tabular-nums" data-testid="recorder-timer">
          {fmt(elapsed)}
        </div>
      </div>

      {/* Waveform area */}
      <div className="bg-bone-200 border border-bone-300 rounded-md h-36 flex items-end justify-center gap-1 px-6 py-4">
        {Array.from({ length: 28 }).map((_, i) => {
          const baseH = 8 + ((i * 13) % 56);
          return (
            <div
              key={`wf-bar-${i}`}
              className="wf-bar"
              style={{
                height: recording ? `${baseH}px` : "6px",
                animation: recording
                  ? `wave ${0.7 + ((i * 17) % 60) / 100}s ease-in-out ${i * 0.04}s infinite`
                  : "none",
                opacity: recording ? 1 : 0.4,
              }}
            />
          );
        })}
      </div>

      <div className="mt-8 flex items-center justify-center gap-4">
        {!recording ? (
          <button
            type="button"
            onClick={start}
            disabled={busy}
            className="relative w-20 h-20 rounded-full bg-brand text-bone-100 flex items-center justify-center hover:bg-brand-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed animate-pulse-soft"
            data-testid="record-start-btn"
            aria-label="Start recording"
          >
            <Microphone size={32} weight="fill" />
          </button>
        ) : (
          <button
            type="button"
            onClick={stop}
            disabled={stopping}
            className="relative w-20 h-20 rounded-full bg-ink-900 text-bone-100 flex items-center justify-center hover:bg-ink-700 transition-all disabled:opacity-60"
            data-testid="record-stop-btn"
            aria-label="Stop recording"
          >
            <Stop size={28} weight="fill" />
          </button>
        )}
        {busy && (
          <span className="flex items-center gap-2 text-ink-500 text-sm" data-testid="recorder-busy">
            <ArrowClockwise className="animate-spin" size={16} />
            Transcribing & analyzing…
          </span>
        )}
      </div>

      <p className="text-center text-xs text-ink-500 mt-6">
        {recording
          ? "Tap stop when you're done. Max 10 minutes."
          : "Tap the microphone to start. Speak freely — Hush will handle the rest."}
      </p>

      {error && (
        <div
          className="mt-5 text-sm text-brand bg-brand/10 border border-brand/20 rounded-md px-3 py-2 text-center"
          data-testid="recorder-error"
        >
          {error}
        </div>
      )}
    </div>
  );
}
